import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Space, theme, Badge } from 'antd';
import {
    DashboardOutlined,
    ToolOutlined,
    ProjectOutlined,
    AppstoreOutlined,
    FileTextOutlined,
    TeamOutlined,
    BankOutlined,
    AimOutlined,
    InfoCircleOutlined,
    PhoneOutlined,
    SafetyOutlined,
    SafetyCertificateOutlined,
    SolutionOutlined,
    LogoutOutlined,
    UserOutlined,
    BellOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Header, Sider, Content } = AntLayout;

export default function Layout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const menuItems = [
        { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' },
        { key: '/services', icon: <ToolOutlined />, label: 'Services' },
        { key: '/portfolio', icon: <ProjectOutlined />, label: 'Portfolio' },
        { key: '/categories', icon: <AppstoreOutlined />, label: 'Categories' },
        { key: '/news', icon: <FileTextOutlined />, label: 'Articles' },
        { key: '/team', icon: <TeamOutlined />, label: 'Team' },
        {
            key: 'company-group',
            icon: <BankOutlined />,
            label: 'Company',
            children: [
                { key: '/company', icon: <BankOutlined />, label: 'Company Info' },
                { key: '/vision-mission', icon: <AimOutlined />, label: 'Vision & Mission' },
                { key: '/about', icon: <InfoCircleOutlined />, label: 'About' },
            ]
        },
        {
            key: 'settings-group',
            icon: <SafetyOutlined />,
            label: 'Settings',
            children: [
                { key: '/contact', icon: <PhoneOutlined />, label: 'Contact' },
                { key: '/legal', icon: <SafetyOutlined />, label: 'Legal' },
                { key: '/certifications', icon: <SafetyCertificateOutlined />, label: 'Certifications' },
                { key: '/careers', icon: <SolutionOutlined />, label: 'Careers' },
            ]
        },
    ];

    const userMenuItems = [
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: 'Profile',
        },
        {
            type: 'divider',
        },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: 'Logout',
            danger: true,
            onClick: handleLogout,
        },
    ];

    const getCurrentKey = () => {
        const path = location.pathname;
        // Check if path matches any menu item
        for (const item of menuItems) {
            if (item.children) {
                for (const child of item.children) {
                    if (path.startsWith(child.key)) return child.key;
                }
            } else if (path.startsWith(item.key)) {
                return item.key;
            }
        }
        return '/dashboard';
    };

    const getOpenKeys = () => {
        const path = location.pathname;
        const openKeys = [];
        for (const item of menuItems) {
            if (item.children) {
                for (const child of item.children) {
                    if (path.startsWith(child.key)) {
                        openKeys.push(item.key);
                    }
                }
            }
        }
        return openKeys;
    };

    return (
        <AntLayout style={{ minHeight: '100vh' }}>
            <Sider
                trigger={null}
                collapsible
                collapsed={collapsed}
                style={{
                    overflow: 'auto',
                    height: '100vh',
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    bottom: 0,
                }}
            >
                <div style={{
                    height: 64,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: collapsed ? 20 : 18,
                    fontWeight: 'bold',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}>
                    {collapsed ? '🏗️' : '🏗️ REKAN CMS'}
                </div>
                <Menu
                    theme="dark"
                    mode="inline"
                    selectedKeys={[getCurrentKey()]}
                    defaultOpenKeys={getOpenKeys()}
                    items={menuItems}
                    onClick={({ key }) => {
                        if (!key.includes('group')) {
                            navigate(key);
                        }
                    }}
                />
            </Sider>
            <AntLayout style={{ marginLeft: collapsed ? 80 : 200 }}>
                <Header
                    style={{
                        padding: '0 24px',
                        background: colorBgContainer,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}
                >
                    <Space>
                        {collapsed ? (
                            <MenuUnfoldOutlined
                                style={{ fontSize: 18, cursor: 'pointer' }}
                                onClick={() => setCollapsed(false)}
                            />
                        ) : (
                            <MenuFoldOutlined
                                style={{ fontSize: 18, cursor: 'pointer' }}
                                onClick={() => setCollapsed(true)}
                            />
                        )}
                    </Space>

                    <Space size="large">
                        <Badge count={0} showZero={false}>
                            <BellOutlined style={{ fontSize: 20, cursor: 'pointer' }} />
                        </Badge>
                        <Dropdown menu={{ items: userMenuItems }} trigger={['click']}>
                            <Space style={{ cursor: 'pointer' }}>
                                <Avatar style={{ backgroundColor: '#1890ff' }}>
                                    {user?.name?.charAt(0).toUpperCase()}
                                </Avatar>
                                <span style={{ fontWeight: 500 }}>{user?.name}</span>
                            </Space>
                        </Dropdown>
                    </Space>
                </Header>
                <Content
                    style={{
                        margin: '24px 16px',
                        padding: 24,
                        minHeight: 280,
                        background: colorBgContainer,
                        borderRadius: borderRadiusLG,
                    }}
                >
                    <Outlet />
                </Content>
            </AntLayout>
        </AntLayout>
    );
}
